import React, { useState, useEffect } from "react";
import axios from "axios";
import dayjs from "dayjs";
import 'dayjs/locale/ro';
import { useLocation } from "react-router-dom";
import { HiOutlineCalendar, HiOutlineCheckCircle, HiOutlineExclamationCircle } from "react-icons/hi2";
import { API_URL } from "../../config";

type PhysicianData = {
    physician_id: number;
    user_name_surname: string;
    email: string;
    phone: number | string;
    birth: string;
    is_active: boolean | number;
    data_acord: boolean | number;
    description?: string;
    role_name?: string;
};

interface PhysiciansInfoProps {
    setFetchUsersCallback: (callback: () => void) => void;
}

const getInitials = (name?: string) => {
    if (!name) return "DR";
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const formatRomanianDate = (dateStr?: string) => {
    if (!dateStr) return "Nespecificată";
    const months = [
        "ianuarie", "februarie", "martie", "aprilie", "mai", "iunie",
        "iulie", "august", "septembrie", "octombrie", "noiembrie", "decembrie"
    ];
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const day = d.getDate();
    const month = months[d.getMonth()];
    const year = d.getFullYear();
    return `${day} ${month} ${year}`;
};

export const PhysiciansInfo: React.FC<PhysiciansInfoProps> = ({ setFetchUsersCallback }) => {
    const location = useLocation();
    const { item: initialData } = location.state || {};
    const [selectedEdit, setSelectedEdit] = useState(initialData);
    const [selectedEditRowData, setSelectedEditRowData] = useState<PhysicianData | null>(initialData || null);
    const [showGrid, setShowGrid] = useState(true);

    const token = localStorage.getItem('jwtToken');

    const fetchPhysician = async () => {
        if (!selectedEdit?.physician_id) return;
        try {
            const response = await axios.get(`${API_URL}/api/physician`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });
            const found = response.data.find((item: any) => item.physician_id === selectedEdit.physician_id);
            if (found) {
                setSelectedEditRowData(found);
            }
        } catch (error) {
            console.error("Error fetching physician data:", error);
        }
    };

    useEffect(() => {
        fetchPhysician();
    }, [selectedEdit]);

    useEffect(() => {
        setFetchUsersCallback(() => fetchPhysician);
    }, [setFetchUsersCallback]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setShowGrid(false);
        }, 350);
        return () => clearTimeout(timer);
    }, []);

    const displayName = selectedEditRowData?.user_name_surname || "Medic";
    const isActive = selectedEditRowData?.is_active !== undefined ? Boolean(selectedEditRowData.is_active) : true;
    const hasConsent = selectedEditRowData?.data_acord !== undefined ? Boolean(selectedEditRowData.data_acord) : false;
    const birthFormatted = formatRomanianDate(selectedEditRowData?.birth);
    const initials = getInitials(displayName);

    return (
        <div className="w-full h-full">
            {showGrid ? (
                <div className="bg-white dark:bg-gray-800 border border-[#E7EAF0] dark:border-gray-700/60 rounded-2xl p-6 shadow-xs animate-pulse flex flex-col justify-between h-full min-h-[300px]">
                    <div className="flex justify-end">
                        <div className="w-16 h-6 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                    </div>
                    <div className="flex flex-col items-center">
                        <div className="w-28 h-28 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                        <div className="w-36 h-5 bg-gray-200 dark:bg-gray-700 rounded-md mt-4"></div>
                    </div>
                    <div className="w-full h-14 bg-gray-100 dark:bg-gray-700/50 rounded-xl mt-6"></div>
                </div>
            ) : (
                <div className="bg-white dark:bg-gray-800 border border-[#E7EAF0] dark:border-gray-700/60 rounded-2xl p-6 shadow-xs relative flex flex-col justify-between h-full min-h-[300px] transition-all duration-200">
                    {/* Status Badge Top-Right */}
                    <div className="absolute top-6 right-6">
                        <span className={`px-3 py-1 rounded-lg text-xs font-semibold ${
                            isActive
                                ? "bg-[#ECE8FF] text-[#6755E8] dark:bg-indigo-950/70 dark:text-indigo-300"
                                : "bg-rose-50 text-rose-600 dark:bg-rose-950/70 dark:text-rose-300"
                        }`}>
                            {isActive ? "Medic Activ" : "Inactiv"}
                        </span>
                    </div>

                    {/* Center: Large Soft Rounded Avatar & Doctor Name */}
                    <div className="flex flex-col items-center pt-2">
                        <div className="w-[112px] h-[112px] rounded-full p-2 bg-[#F8FAFC] dark:bg-gray-700/30 flex items-center justify-center">
                            <div className="w-full h-full rounded-full bg-[#ECE8FF] dark:bg-indigo-950/70 text-[#6755E8] dark:text-indigo-300 font-bold text-3xl flex items-center justify-center tracking-tight">
                                {initials}
                            </div>
                        </div>

                        <h2 className="text-[17px] font-bold text-[#1E293B] dark:text-gray-100 mt-4 text-center tracking-tight">
                            {displayName}
                        </h2>
                    </div>

                    {/* Bottom Meta Box: Data nașterii & Acord date */}
                    <div className="mt-6 p-3 bg-white dark:bg-gray-800/80 border border-[#E2E8F0] dark:border-gray-700 rounded-xl flex items-center justify-between">
                        {/* Left: Data nașterii */}
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg bg-[#ECE8FF] dark:bg-indigo-950/60 text-[#6755E8] dark:text-indigo-400 flex items-center justify-center shrink-0">
                                <HiOutlineCalendar className="w-4 h-4" />
                            </div>
                            <div>
                                <span className="text-[9px] font-semibold text-gray-400 uppercase tracking-wider block leading-none mb-1">
                                    DATA NAȘTERII
                                </span>
                                <span className="text-xs font-semibold text-[#1E293B] dark:text-gray-200 leading-none">
                                    {birthFormatted}
                                </span>
                            </div>
                        </div>

                        {/* Right: Acord date */}
                        <div className="flex items-center gap-1.5 pl-3 border-l border-[#E2E8F0] dark:border-gray-700">
                            {hasConsent ? (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-bold bg-[#DCFCE7] text-[#15803D] dark:bg-emerald-950/60 dark:text-emerald-300" title="Acordul de marketing este activ și confirmat">
                                    <HiOutlineCheckCircle className="w-3.5 h-3.5" />
                                    <span>Acord valid</span>
                                </span>
                            ) : (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-bold bg-[#FEF3C7] text-[#B45309] dark:bg-amber-950/60 dark:text-amber-300" title="Nu există acord de date marketing confirmat">
                                    <HiOutlineExclamationCircle className="w-3.5 h-3.5" />
                                    <span>Fără acord</span>
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
